<template>
  <div class="section-aside__container" data-sticky>

    <div class="section-aside__cont">
      <div class="individual-packing__title" ref="title">{{ stageData.name }}</div>
      <div class="form individual-packing__step">

        <template v-for="group in stageData.groups" :key="group.code">
          <IPFormGroup v-if="getFieldsGroupVisibility(group.code)" :group-class="getFieldsGroupSizeClass(group.code)" :hint="group.hint" :link="group.link" :title="group.name">

            <template v-if="getSwitches(group).length" #formSwitch>

              <InputSwitch v-for="item in getSwitches(group)" :key="item.code"
                           :options="item.enum"
                           :name="item.code"
                           :model-value="getFormValueByKey(item.code)"
                           @update:model-value="setValue(item.code, 'switch', $event)"
              />
            </template>

            <template v-slot:[getFieldsView(group.code)] >
              <template v-for="field in group.fields" :key="field.id">
                <template v-if="getFieldVisibility(field.code)">
                  <template v-if="field.type === 'text' || field.type === 'number'">
                    <div :class="getFieldClass(group.code, field.code)">
                      <InputText :placeholder="field.placeholder || field.name"
                                 :label="field.name"
                                 :required="field.isRequired"
                                 :name="field.code"
                                 :inputmode="field.type === 'number' ? 'numeric' : 'text'"
                                 :mask-settings="getFieldMask(field.code)"
                                 :model-value="getFormValueByKey(field.code)"
                                 :errors="v$.formState[field.code].$errors"
                                 @update:model-value="setValue(field.code, field.type, $event)"
                      />
                    </div>
                  </template>

                  <template v-if="field.type === 'file'">
                    <div :class="getFieldClass(group.code, field.code)">
                      <InputFile :placeholder="field.placeholder || field.name"
                                 :accept="field.access"
                                 :multiple="field.isMultiple"
                                 :required="field.isRequired"
                                 :id="field.code"
                                 :name="field.code"
                                 :model-value="getFormValueByKey(field.code)"
                                 :errors="v$.formState[field.code].$errors"
                                 @update:model-value="setValue(field.code, field.type, $event)"
                      />
                    </div>
                  </template>

                  <template v-if="field.type === 'textarea'">
                    <div :class="getFieldClass(group.code, field.code)">
                      <InputTextarea :placeholder="field.placeholder || field.name"
                                     :label="field.name"
                                     :required="field.isRequired"
                                     :name="field.code"
                                     :model-value="getFormValueByKey(field.code)"
                                     :errors="v$.formState[field.code].$errors"
                                     @update:model-value="setValue(field.code, field.type, $event)"
                      />
                    </div>
                  </template>

                  <template v-if="field.type === 'range'">
                    <div :class="getFieldClass(group.code, field.code)">
                      <InputRange :placeholder="field.placeholder || field.name"
                                  :label="field.name"
                                  :mask-settings="getFieldMask(field.code)"
                                  :max="field.max"
                                  :min="field.min"
                                  :start="getFormValueByKey(field.code)"
                                  :step="field.step"
                                  :required="field.isRequired"
                                  :name="field.code"
                                  :model-value="getFormValueByKey(field.code)"
                                  :errors="v$.formState[field.code].$errors"
                                  @update:model-value="setValue(field.code, field.type, $event)"
                      />
                    </div>
                  </template>

                  <template v-if="field.type === 'select'">
                    <div :class="getFieldClass(group.code, field.code)">
                      <InputSelect :label="field.name"
                                   :required="field.isRequired"
                                   :errors="v$.formState[field.code].$errors"
                                   :value="getFormValueByKey(field.code)"
                      >
                        <Multiselect :placeholder="field.placeholder || field.name"
                                     :options="getFieldEnums(field)"
                                     :groups="shouldBeGroupped(field.code)"
                                     no-options-text="Нет доступных вариантов"
                                     no-results-text="Нет результатов по вашему запросу"
                                     value-prop="id"
                                     label="name"
                                     searchable
                                     :model-value="getFormValueByKey(field.code)"
                                     @update:model-value="setValue(field.code, field.type, $event)"
                        />
                      </InputSelect>
                    </div>
                  </template>

                  <template v-if="field.type === 'radio'">
                    <div :class="getFieldClass(group.code, field.code)" v-for="radioItem in field.enum" :key="radioItem.id">
                      <div :class="`${getFieldClass(group.code, field.code)}-input`">
                        <InputRadio
                            :label="radioItem.name"
                            :id="`${field.code}-${radioItem.id}`"
                            :value="radioItem.id"
                            :icon="radioItem.icon"
                            :show-icon="!!field.enum[0]?.icon"
                            :name="field.code"
                            :disabled="checkDisabled(field, radioItem)"
                            :hint-text="radioItem.hint"
                            :text="radioItem.description"
                            :required="field.isRequired"
                            :model-value="getFormValueByKey(field.code)"
                            :errors="v$.formState[field.code].$errors"
                            @update:model-value="setValue(field.code, field.type, $event)"
                        />
                      </div>
                      <div v-if="radioItem.text" :class="`${getFieldClass(group.code, field.code)}-text`">
                        <span>{{ radioItem.text }}</span>
                      </div>
                    </div>
                  </template>

                  <template v-if="field.type === 'checkbox'">
                    <div :class="getFieldClass(group.code, field.code)" v-for="checkboxItem in field.enum" :key="checkboxItem.id">
                      <div :class="`${getFieldClass(group.code, field.code)}-input`">
                        <InputCheckbox
                            :label="checkboxItem.name"
                            :id="`${field.code}-${checkboxItem.id}`"
                            :value="checkboxItem.id"
                            :icon="checkboxItem.icon"
                            :show-icon="!!field.enum[0]?.icon"
                            :name="field.code"
                            :disabled="checkDisabled(field, checkboxItem)"
                            :hint-text="checkboxItem.hint"
                            :text="checkboxItem.description"
                            :required="field.isRequired"
                            :model-value="getFormValueByKey(field.code)"
                            :errors="v$.formState[field.code].$errors"
                            @update:model-value="setValue(field.code, field.type, $event)"
                        />
                      </div>
                      <div v-if="checkboxItem.text" :class="`${getFieldClass(group.code, field.code)}-text`">
                        <span>{{ checkboxItem.text }}</span>
                      </div>
                    </div>
                  </template>
                </template>
              </template>
            </template>
          </IPFormGroup>
        </template>

      </div>
    </div>

    <IPAside>
      <button class="default-btn default-btn--dark" @click.prevent="$emit('prev')">
        <svg class="default-btn__icon" aria-hidden="true">
          <use xlink:href="/local/templates/xpage/img/icons/icons.svg#svg-arrow-left"></use>
        </svg>
        {{ stageBackTitle }}
      </button>
      <button class="default-btn default-btn--green" :disabled="v$.$dirty && v$.$error" @click.prevent="goNext">
        {{ stageNextTitle }}
        <svg class="default-btn__icon" aria-hidden="true">
          <use xlink:href="/local/templates/xpage/img/icons/icons.svg#svg-arrow-right"></use>
        </svg>
      </button>
    </IPAside>
  </div>
</template>

<script>
import IPFormGroup from "@/components/individual-packing/utils/IPFormGroup.vue";
import {IPFieldsView} from "@/mixins/IPFieldsView";
import InputText from "@/components/utils/formElements/InputText.vue";
import {useIndividualPacking} from "@/hooks/useIndividualPacking";
import InputTextarea from "@/components/utils/formElements/InputTextarea.vue";
import InputRange from "@/components/utils/formElements/InputRange.vue";
import InputRadio from "@/components/utils/formElements/InputRadio.vue";
import IPAside from "@/components/individual-packing/utils/IPAside.vue";
import InputFile from "@/components/utils/formElements/InputFile.vue";
import InputCheckbox from "@/components/utils/formElements/InputCheckbox.vue";
import { useVuelidate } from '@vuelidate/core'
import {IPFieldsRules} from "@/mixins/IPFieldsRules";
import InputSelect from "@/components/utils/formElements/InputSelect.vue";
import Multiselect from "@vueform/multiselect"
import InputSwitch from "@/components/utils/formElements/InputSwitch.vue";

export default {
  name: "IPStage",
  emits: ['next', 'prev'],
  mixins: [IPFieldsView, IPFieldsRules, useIndividualPacking],
  components: {
    InputSwitch,
    InputSelect,
    InputCheckbox, InputFile, IPAside, InputRadio, InputRange, Multiselect, InputTextarea, InputText, IPFormGroup
  },
  props: {
    formType: {
      type: String,
      required: true,
      default: 'full'
    },
    stageData: {
      type: Object,
      required: true,
      default: () => ({
        code: '',
        groups: [],
        id: null,
        name: '',
        sort: 10,
      })
    },
    stageBackTitle: {
      type: [String],
      default: BX.message('CATALOG_INDIVIDUAL_BACK')
    },
    stageNextTitle: {
      type: [String],
      default: BX.message('CATALOG_INDIVIDUAL_NEXT')
    },
  },
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  validations() {
    const obj = {};
    this.stageData.groups.forEach(group => {
      group.fields.forEach(field => {
        obj[field.code] = {
          ...this.getFieldRules(field),
        };
      });
    });

    return {formState: obj};
  },
  computed: {
    formState() {
      const formState = {};
      this.stageData.groups.forEach(group => {
        group.fields.forEach(field => {
          formState[field.code] = this.getFormValueByKey(field.code);
        })
      })
      return formState;
    },
    getSwitches() {
      return (group) => {
        const switchesArr = [];
        group.fields.forEach(field => {
          if (field.type === 'switch') {
            switchesArr.push(field);
          }
        })
        return switchesArr;
      }
    },
  },
  methods: {
    setValue(fieldCode, fieldType, eventVal) {
      if (fieldCode === 'COUNT_LAYERS') {
        this.setFormKey({key: 'PROFILE_CARDBOARD', type: 'radio', value: null});
      }
      this.setFormKey({key: fieldCode, type: fieldType, value: eventVal});
    },
    goNext() {
      this.v$.$validate().then((result) => {
        if (result) {
          this.$emit('next');
        } else {
          const inputWithError = document.querySelector('.error');
          if (inputWithError) {
            inputWithError.scrollIntoView({block: "center", behavior: "smooth"});
          }
        }
      })
    },
  },
  mounted() {
    if (this.$refs.title) {
      this.$refs.title.scrollIntoView({block: "center", behavior: "smooth"})
    }
  }
}
</script>

<style scoped>

</style>
